/**
 * What a realtime bar did to the stored bars (see BarStore.applyRealtimeBar).
 */
export const REALTIME_CHANGE = {
  LAST_BAR_UPDATED: "lastBarUpdated",
  BAR_APPENDED: "barAppended",
};

/**
 * Bars of the current symbol and resolution, oldest first.
 * Bar: { time (ms, open time), open, high, low, close, volume }.
 */
export default class BarStore {
  constructor() {
    this.bars = [];
  }

  /** @param {object[]} bars - sorted by time, oldest first */
  setBars(bars) {
    this.bars = bars;
  }

  /**
   * Adds an older page of history on the left. Bars not older than the first
   * stored bar are dropped, so pages that overlap at their edge do not repeat.
   *
   * @param {object[]} olderBars - sorted by time, oldest first
   * @returns {number} how many bars were added
   */
  prependBars(olderBars) {
    const firstTime = this.bars.length ? this.bars[0].time : Infinity;
    const newBars = olderBars.filter((bar) => bar.time < firstTime);

    if (newBars.length) this.bars = newBars.concat(this.bars);

    return newBars.length;
  }

  /**
   * Applies a bar from the realtime stream: the same time replaces the last
   * bar, a later time adds a bar on the right, an earlier time is ignored.
   *
   * @param {object} bar
   * @returns {string|null} one of REALTIME_CHANGE, null when nothing changed
   */
  applyRealtimeBar(bar) {
    const lastBar = this.bars[this.bars.length - 1];

    if (lastBar && bar.time < lastBar.time) return null;

    if (lastBar && bar.time === lastBar.time) {
      this.bars[this.bars.length - 1] = bar;
      return REALTIME_CHANGE.LAST_BAR_UPDATED;
    }

    this.bars.push(bar);
    return REALTIME_CHANGE.BAR_APPENDED;
  }

  get length() {
    return this.bars.length;
  }

  get lastIndex() {
    return this.bars.length - 1;
  }

  /**
   * Open time of the bar at `index`; outside the stored bars it continues
   * from the nearest bar with a step of `barDurationMs` (crosshair over the
   * empty space right of the last bar).
   *
   * @param {number} index
   * @param {number} barDurationMs
   * @returns {number|null} ms, null when there are no bars
   */
  timeAt(index, barDurationMs) {
    if (!this.bars.length) return null;

    const nearestIndex = Math.min(this.lastIndex, Math.max(0, index));

    return this.bars[nearestIndex].time + (index - nearestIndex) * barDurationMs;
  }
}
